import { useState } from 'react';
import { sumBy, isNil } from 'lodash';
import PageContainer from 'components/PageContainer';
import Table, {
  TableActions,
  useStyles as useTableStyles,
} from 'components/Table';
import { ViewQuilt, Edit, CloudDownload } from '@mui/icons-material';
import {
  createOpenTrancheRoute,
  createClosedTrancheRoute,
  createInvestmentsListByFundRoute,
  createTrancheEditRoute,
} from 'adminConstants/routes';
import { numberToCurrencyString, dateToLabel } from 'further-ui/utils';
import HoverPointer from 'components/HoverPointer';
import { useFunds } from 'hooks/data/fund/useFunds';
import { usePerformanceFeeSimulation } from 'hooks/data/fund/usePerformanceFeeSimulation';
import { useDebounce } from 'usehooks-ts';
import TranchesFilters from './TranchesFilters';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import { useFund } from 'hooks/data/fund/useFund';
import useApiExceptionHandler from 'hooks/ui/useApiExceptionHandler';
import { Box, IconButton, Tooltip } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import PageContent from 'components/PageContent';
import Dialog from 'components/Dialog';
import { FilterList } from '@mui/icons-material';
import FilterPanel from './FilterPanel';
import { useGetRole } from 'hooks/ui/useGetRole';
import { useDisclosure, useFilters, usePagination } from 'further-ui/hooks';
import { CalendarDay } from 'further-ui/utils';
import { FundStatus } from 'adminConstants/fundProperties';
import { SortOrder } from 'further-types/utils';

type Filters = {
  firmId?: string;
  fundName: string | null;
  snapshotDate: Date | null;
  tagId: { _id: string; name: string } | null;
  status: { fundStatus?: boolean; fullyDeployed?: boolean } | null;
};

const breadcrumbs = [
  { label: 'Dashboard', link: '/' },
  { label: 'All Tranches', isActive: true },
];

const currentValueTooltipText = (
  <>
    This is the combined value of all current holdings, exits and uninvested
    paid in capital. Where there are unpaid commitments, this figure may be
    materially lower than <em>Initial value</em>, <em>Invested capital</em> or{' '}
    <em>Uninvested capital</em>.
  </>
);

const AllTranches: React.FC = () => {
  const [updatePermission] = useHasPermissions([RolePermission.EditTranche]);
  const { classes: tableClasses } = useTableStyles();
  const exceptionHandler = useApiExceptionHandler();

  const { firmId } = useGetRole();

  const filterDisclosure = useDisclosure();

  const columns = [
    {
      label: 'Firm name',
      key: 'firmId.firmName',
      render: (elm) => <>{elm?.firmId?.firmName}</>,
    },
    { label: 'Tranche name', key: 'fundName' },
    {
      label: 'Close date',
      key: 'closedDate',
      render: (elm) =>
        elm?.fundStatus ? 'Open' : dateToLabel(elm.closedDate, 'Not found'),
    },
    {
      label: 'Subscriptions',
      key: 'investmentsCount',
      sort: false,
      render: (elm) => {
        return elm?.investmentsCount > 0 ? (
          <HoverPointer to={createInvestmentsListByFundRoute(elm?._id)}>
            {elm?.investmentsCount}
          </HoverPointer>
        ) : (
          <span>{elm?.investmentsCount}</span>
        );
      },
    },
    {
      label: 'Companies',
      key: 'companiesInTranche',
      sort: false,
      render: (elm) =>
        elm?.companyCount > 0 ? (
          <HoverPointer to={`/company?fundId=${elm?._id}`}>
            {elm?.companyCount}
          </HoverPointer>
        ) : (
          <span>{elm?.companyCount}</span>
        ),
    },
    {
      label: 'Total subscription value',
      key: 'totalInvestment',
      sort: false,
      render: (elm) =>
        numberToCurrencyString(elm.investmentBalances?.totalInvestment, {
          fallback: 'N/A',
        }),
    },
    {
      label: 'Paid-in value',
      key: 'initialValue',
      sort: false,
      render: (elm) =>
        numberToCurrencyString(
          elm.investmentBalances?.initialInvestmentAmount,
          {
            fallback: 'N/A',
          },
        ),
    },
    {
      label: (
        <Box display="flex" alignItems="center" gap="4px">
          <span>Current value</span>
          <Tooltip title={currentValueTooltipText}>
            <IconButton size="small">
              <InfoOutlined style={{ fontSize: '1rem' }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      key: 'currentValue',
      sort: false,
      render: (elm) =>
        numberToCurrencyString(elm.investmentBalances?.investmentValue, {
          fallback: 'N/A',
        }),
    },
    {
      label: 'Realised value',
      key: 'realisedValue',
      sort: false,
      render: (elm) =>
        numberToCurrencyString(elm.investmentBalances?.realisedValue, {
          fallback: 'N/A',
        }),
    },
    {
      label: 'Invested capital',
      key: 'investedCapital',
      sort: false,
      render: (elm) =>
        numberToCurrencyString(elm.investmentBalances?.investedCapital, {
          fallback: 'N/A',
        }),
    },
    {
      label: 'Uninvested capital',
      key: 'uninvestedCapital',
      sort: false,
      render: (elm) =>
        numberToCurrencyString(elm.investmentBalances?.uninvestedCapital, {
          fallback: 'N/A',
        }),
    },
    {
      label: 'Actions',
      key: 'actions',
      sort: false,
      render: (elm) => (
        <TableActions
          showAsDropdown
          actions={[
            {
              visible: updatePermission,
              link: createTrancheEditRoute(elm._id),
              icon: Edit,
              color: 'primary',
              label: 'Edit',
            },

            {
              link: elm?.fundStatus
                ? createOpenTrancheRoute(elm?._id)
                : createClosedTrancheRoute(elm?._id),
              icon: ViewQuilt,
              color: 'primary',
              label: 'View dashboard',
            },
            {
              onClick: () =>
                exportFund(elm?._id, elm?.fundName, elm?.firmId?.firmName),
              icon: CloudDownload,
              color: 'primary',
              label: 'Export',
            },
          ]}
        />
      ),
    },
  ];

  const [isExportLoading, setExportLoading] = useState(false);
  const pagination = usePagination({
    id: 'all-tranches',
    orderBy: 'closedDate',
    order: SortOrder.Desc,
  });

  const defaultFilters = {
    firmId,
    fundName: null,
    snapshotDate: null,
    tagId: null,
    status: null,
  };

  const advancedFilterKeys: Array<keyof Filters> = ['firmId'];

  const {
    filters,
    handleChangeFilters,
    advancedFiltersApplied,
    resetAdvancedFilters,
  } = useFilters<Filters>('all-tranches', {
    defaultFilters,
    onFiltersChange: pagination.toFirstPage,
    advancedFilterKeys,
  });

  const debouncedFundName = useDebounce(filters.fundName, 150);
  const queryParams = {
    firmId: filters.firmId || firmId,
    fundName: debouncedFundName,
    tagId: filters?.tagId?._id,
    snapshotDate: filters.snapshotDate,
    keyName: pagination.orderBy,
    type: pagination.order,
    includeTrancheValue: true,
    includeInvestmentsSummary: true,
    fundStatus: !isNil(filters?.status?.fundStatus)
      ? [String(filters.status.fundStatus)]
      : undefined,
    fullyDeployed: filters?.status?.fullyDeployed ?? undefined,
  };

  const { tranches, exportFunds } = useFunds({
    page: pagination.page === 0 ? 1 : pagination.page,
    perPage: pagination.rowsPerPage,
    ...queryParams,
  });
  const { exportFund } = useFund();
  const { exportPerformanceFeeSimulation } = usePerformanceFeeSimulation();

  const exportExcelData = async () => {
    try {
      setExportLoading(true);
      await exportFunds({
        ...queryParams,
        includeExportData: true,
        includeChargedPerformanceFees: true,
        filenameDate: filters.snapshotDate ?? undefined,
      });
    } catch (e: any) {
      exceptionHandler(e, 'Error when exporting subscription transfer', true);
    } finally {
      setExportLoading(false);
    }
  };

  const handlePerformanceFeeSimulation = async () => {
    try {
      setExportLoading(true);

      const params = {
        firmId: filters.firmId ?? undefined,
        fundName: filters.fundName ?? undefined,
        tagId: filters?.tagId?._id ?? undefined,
        snapshotDate: filters.snapshotDate
          ? new CalendarDay(filters.snapshotDate)
          : undefined,
        fullyDeployed: filters?.status?.fullyDeployed ?? undefined,
        fundStatus: filters?.status?.fundStatus
          ? [
              filters.status.fundStatus
                ? String(FundStatus.OPEN)
                : String(FundStatus.CLOSED),
            ]
          : undefined,
      };

      await exportPerformanceFeeSimulation(params);
    } catch (e: unknown) {
      exceptionHandler(
        e instanceof Error ? e : new Error('Unknown error'),
        'Error when exporting performance fee simulation',
        true,
      );
    } finally {
      setExportLoading(false);
    }
  };

  const FooterSummary = () => (
    <tfoot>
      <tr className={tableClasses.tFootRow}>
        <td colSpan={5} />
        <td className={tableClasses.tFootCell}>
          {numberToCurrencyString(
            sumBy(tranches?.data, 'investmentBalances.totalInvestment'),
          )}
        </td>
        <td className={tableClasses.tFootCell}>
          {numberToCurrencyString(
            sumBy(tranches?.data, 'investmentBalances.initialInvestmentAmount'),
          )}
        </td>
        <td className={tableClasses.tFootCell}>
          {numberToCurrencyString(
            sumBy(tranches?.data, 'investmentBalances.investmentValue'),
          )}
        </td>
        <td className={tableClasses.tFootCell}>
          {numberToCurrencyString(
            sumBy(tranches?.data, 'investmentBalances.realisedValue'),
          )}
        </td>
        <td className={tableClasses.tFootCell}>
          {numberToCurrencyString(
            sumBy(tranches?.data, 'investmentBalances.investedCapital'),
          )}
        </td>
        <td className={tableClasses.tFootCell}>
          {numberToCurrencyString(
            sumBy(tranches?.data, 'investmentBalances.uninvestedCapital'),
          )}
        </td>
        <td />
      </tr>
    </tfoot>
  );

  const filterButtonLabel = advancedFiltersApplied ? (
    <img
      src="images/customIcons/filter-applied.png"
      alt="filters applied"
      width="24px"
      height="24px"
    />
  ) : (
    <FilterList />
  );

  return (
    <PageContainer heading="All Tranches" breadcrumbs={breadcrumbs}>
      <PageContent>
        <TranchesFilters
          onFiltersChange={handleChangeFilters}
          filters={filters}
          onExportClick={exportExcelData}
          onPerformanceFeeSimulationClick={handlePerformanceFeeSimulation}
          isExportLoading={isExportLoading}
          filterButtonLabel={filterButtonLabel}
          onOpenFilter={filterDisclosure.onOpen}
        />
        <Table
          columns={columns}
          onRequestSort={pagination.handleRequestSort}
          order={pagination.order}
          orderBy={pagination.orderBy}
          tablebody={tranches?.data ?? []}
          onPageChange={pagination.handleChangePage}
          onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          page={pagination.page}
          rowsPerPage={pagination.rowsPerPage}
          count={tranches?.total ?? 0}
          pagination={true}
          loading={tranches.isFetching}
          emptyMessage="There are currently no tranches"
          variant="nohover"
          TableFooter={<FooterSummary />}
        />
        <Dialog
          open={filterDisclosure.isOpen}
          title="Advanced Filters"
          onClose={filterDisclosure.onClose}
          onCancel={() => {
            resetAdvancedFilters();
            filterDisclosure.onClose();
          }}
          onConfirm={() => {
            filterDisclosure.onClose();
          }}
          btnLabels={{ cancel: 'Reset', confirm: 'Apply' }}
        >
          <FilterPanel
            filters={filters}
            handleChangeFilters={handleChangeFilters}
          />
        </Dialog>
      </PageContent>
    </PageContainer>
  );
};

export default AllTranches;
